import { saveAs } from "file-saver";
import { pdf } from "@react-pdf/renderer";
import React from "react";
import { exportEvvRecords } from "../../services/UserTracking";
import { decrypt } from "../../utils";
import { EvvRecordsPdf } from "./EvvRecordsPdf";

// Shared EVV export logic — used by the Export dropdown (and the legacy download
// modal). Fetches the filtered records and writes them to the chosen format.

const getStaffName = (staff) =>
  staff?.name || `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim() || "N/A";

const getClientName = (client) => {
  if (!client) return "N/A";
  const f1 = client?.form1;
  if (f1?.firstName || f1?.lastName) return `${f1.firstName || ""} ${f1.lastName || ""}`.trim();
  if (f1?.name) return f1.name;
  if (client?.name) {
    try {
      return decrypt(client.name, false) || client.name;
    } catch {
      return client.name;
    }
  }
  return "N/A";
};

const fmtTs = (ts) => (ts ? new Date(ts).toISOString().replace("T", " ").slice(0, 19) : "");
const fmtDist = (d) => (d != null ? `${Number(d).toFixed(2)} mi` : "");

export const buildExportRecords = (allRecords) =>
  allRecords.map((r) => {
    const ci = r.evvClockIn;
    const co = r.evvClockOut;
    return {
      staffName: getStaffName(r?.staff),
      date: r.date?.date ? new Date(r.date.date).toISOString().split("T")[0] : "",
      facility: r.facility?.name || "",
      type: r.checkin_type || "",
      clientName: getClientName(r.client),
      service: r.service || "",
      evvEnforced: r.evvEnforced ? "Yes" : "No",
      status: r.endTime ? "Completed" : "Still Clocked In",
      clockInTime: fmtTs(ci?.timestamp),
      clockInLat: ci?.location?.lat ?? "",
      clockInLng: ci?.location?.lng ?? "",
      clockInAccuracy: ci?.location?.accuracy ?? "",
      clockInDistance: fmtDist(ci?.distanceToSite),
      clockInAddress: ci?.matchedAddress || "",
      clockInDeviceType: ci?.deviceInfo?.deviceType || "",
      clockInLocationSource: ci?.deviceInfo?.locationSource || "",
      clockInIp: ci?.deviceInfo?.ip || "",
      clockOutTime: fmtTs(co?.timestamp),
      clockOutLat: co?.location?.lat ?? "",
      clockOutLng: co?.location?.lng ?? "",
      clockOutAccuracy: co?.location?.accuracy ?? "",
      clockOutDistance: fmtDist(co?.distanceToSite),
      clockOutAddress: co?.matchedAddress || "",
      clockOutDeviceType: co?.deviceInfo?.deviceType || "",
      clockOutLocationSource: co?.deviceInfo?.locationSource || "",
      clockOutIp: co?.deviceInfo?.ip || "",
      serviceAddress: ci?.serviceAddress || co?.serviceAddress || "",
    };
  });

// Fetch (with the current table filters) and download EVV records in `format`.
// Returns the number of records exported (0 = nothing matched the filters).
export const downloadEvvRecords = async ({ format = "csv", query, filterFacilityName }) => {
  const allRecords = await exportEvvRecords(query);
  if (!allRecords?.length) return 0;

  const exportRows = buildExportRecords(allRecords);

  let resolvedFacilityName = filterFacilityName;
  if (!resolvedFacilityName && exportRows.length > 0) {
    const uniqueFacilities = [...new Set(exportRows.map((r) => r.facility).filter(Boolean))];
    if (uniqueFacilities.length === 1) resolvedFacilityName = uniqueFacilities[0];
  }

  const fileBase = resolvedFacilityName
    ? `${resolvedFacilityName.replace(/[^a-zA-Z0-9 ]/g, "")}_EVV_Records_${query.dateFrom || "all"}_${query.dateTo || "all"}`
    : `EVV_Records_${query.dateFrom || "all"}_${query.dateTo || "all"}`;

  if (format === "pdf") {
    const doc = (
      <EvvRecordsPdf data={exportRows} dateRange={{ start: query.dateFrom, end: query.dateTo }} facilityName={resolvedFacilityName} />
    );
    const asPdf = pdf();
    asPdf.updateContainer(doc);
    const blob = await asPdf.toBlob();
    saveAs(blob, `${fileBase}.pdf`);
    return exportRows.length;
  }

  if (format === "json") {
    const blob = new Blob([JSON.stringify(exportRows, null, 2)], { type: "application/json;charset=utf-8" });
    saveAs(blob, `${fileBase}.json`);
    return exportRows.length;
  }

  if (format === "xml") {
    const escXml = (val) =>
      String(val ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<EvvRecords>\n';
    exportRows.forEach((r) => {
      xml += "  <Record>\n";
      Object.entries(r).forEach(([key, val]) => {
        xml += `    <${key}>${escXml(val)}</${key}>\n`;
      });
      xml += "  </Record>\n";
    });
    xml += "</EvvRecords>";
    saveAs(new Blob([xml], { type: "application/xml;charset=utf-8" }), `${fileBase}.xml`);
    return exportRows.length;
  }

  // CSV default
  const headers = [
    "Staff Name", "Date", "Facility", "Type", "Client Name", "Service",
    "EVV Enforced", "Status",
    "Clock-In Time", "Clock-In Lat", "Clock-In Lng", "Clock-In Accuracy",
    "Clock-In Distance", "Clock-In Address", "Clock-In Device", "Clock-In Location Source", "Clock-In IP",
    "Clock-Out Time", "Clock-Out Lat", "Clock-Out Lng", "Clock-Out Accuracy",
    "Clock-Out Distance", "Clock-Out Address", "Clock-Out Device", "Clock-Out Location Source", "Clock-Out IP",
    "Service Address",
  ];
  const escCsv = (val) => {
    const s = String(val ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [headers.join(",")];
  exportRows.forEach((r) => rows.push(Object.values(r).map(escCsv).join(",")));
  saveAs(new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" }), `${fileBase}.csv`);
  return exportRows.length;
};
